import { useEffect, useMemo } from 'react'
import { PageData } from '@/api/Pagination/Types'
import { RewardsHubDto } from '@/api/Trading-Accounts/Types/RewardsHubDto'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  fetchCountriesAction,
  fetchTickmillCompaniesAction,
} from '@/store/identity/identity.actions'
import {
  fetchRewardProgramResetCadence,
  fetchRewardProgramStatuses,
  fetchRewardProgramTierConditionsLogic,
  fetchRewardProgramTierLogicalOperators,
  fetchRewardTrackingMetrics,
  getCurrenciesAction,
} from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { isEmpty } from '@/utils/is-empty'

export const useRewardProgramOptions = (rewardsList?: PageData<RewardsHubDto[]>) => {
  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const rewardProgramStatusesResult = useAppSelector(
    (state) => state.tradingAccounts.rewardProgramStatuses
  )
  const rewardProgramTrackingMetricsResult = useAppSelector(
    (state) => state.tradingAccounts.rewardTrackingMetrics
  )
  const rewardProgramTierConditionsLogicResult = useAppSelector(
    (state) => state.tradingAccounts.rewardProgramTierConditionsLogic
  )
  const rewardProgramResetCadenceResult = useAppSelector(
    (state) => state.tradingAccounts.rewardProgramResetCadence
  )

  const rewardProgramTierLogicalOperatorsResult = useAppSelector(
    (state) => state.tradingAccounts.rewardProgramTierLogicalOperators
  )

  const currencies = useAppSelector((state) => state.tradingAccounts.currencies)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const countries = useAppSelector((state) => state.identity.countries)

  useEffect(() => {
    const fetchData = async () => {
      if (isEmpty(rewardProgramStatusesResult)) {
        await dispatch(fetchRewardProgramStatuses(tradingAccountsApiClient))
      }
      if (isEmpty(rewardProgramTrackingMetricsResult)) {
        await dispatch(fetchRewardTrackingMetrics(tradingAccountsApiClient))
      }
      if (isEmpty(rewardProgramTierConditionsLogicResult)) {
        await dispatch(fetchRewardProgramTierConditionsLogic(tradingAccountsApiClient))
      }
      if (isEmpty(rewardProgramResetCadenceResult)) {
        await dispatch(fetchRewardProgramResetCadence(tradingAccountsApiClient))
      }
      if (isEmpty(rewardProgramTierLogicalOperatorsResult)) {
        await dispatch(fetchRewardProgramTierLogicalOperators(tradingAccountsApiClient))
      }
      if (isEmpty(currencies)) {
        await dispatch(getCurrenciesAction(tradingAccountsApiClient))
      }
      if (isEmpty(tickmillCompanies)) {
        await dispatch(fetchTickmillCompaniesAction(identityApiClient))
      }
      if (isEmpty(countries)) {
        await dispatch(fetchCountriesAction(identityApiClient))
      }
    }

    fetchData().catch(console.error)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const statusOptions = useMemo(
    () => rewardProgramStatusesResult?.map((s) => ({ id: s.id, name: s.name })) ?? [],
    [rewardProgramStatusesResult]
  )

  const currencyOptions = useMemo(
    () => currencies.map((c) => ({ id: c.id, name: c.id })) ?? [],
    [currencies]
  )

  const companyOptions = useMemo(
    () => tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [tickmillCompanies]
  )

  const countryOptions = useMemo(
    () => countries.map((country) => ({ id: country.id, name: country.name })),
    [countries]
  )

  const metricsOptions = useMemo(
    () => rewardProgramTrackingMetricsResult?.map((m) => ({ id: m.id, name: m.name })) ?? [],
    [rewardProgramTrackingMetricsResult]
  )

  const conditionalLogicOptions = useMemo(
    () => rewardProgramTierConditionsLogicResult?.map((c) => ({ id: c.id, name: c.name })) ?? [],
    [rewardProgramTierConditionsLogicResult]
  )

  const rewardsOptions = useMemo(
    () => rewardsList?.items?.map((r) => ({ id: r.id, name: r.internalName })) ?? [],
    [rewardsList]
  )

  const rewardCadenceOptions = useMemo(
    () => rewardProgramResetCadenceResult?.map((o) => ({ id: o.id, name: o.name })) ?? [],
    [rewardProgramResetCadenceResult]
  )

  const rewardProgramTierLogicalOperatorsOptions = useMemo(
    () =>
      rewardProgramTierLogicalOperatorsResult?.map((o) => ({
        id: o.id,
        name: o.name,
      })) ?? [],
    [rewardProgramTierLogicalOperatorsResult]
  )
  return {
    statusOptions,
    currencyOptions,
    companyOptions,
    countryOptions,
    metricsOptions,
    conditionalLogicOptions,
    rewardProgramTierLogicalOperatorsOptions,
    rewardsOptions,
    rewardCadenceOptions,
  }
}
